import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Alert from '@mui/material/Alert';
import InputAdornment from '@mui/material/InputAdornment';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Tooltip from '@mui/material/Tooltip';
import MuiLink from '@mui/material/Link';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { Search, Info, FileUp, CircleCheck, FileText, Home, ArrowRightLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import useDateFormat from '../../utils/useDateFormat';
import { useAuth } from '../../contexts/AuthContext';
import { IVehicle, VehicleStatus } from '../../../../shared/types/vehicle.types';
import useVehicle from '../../wrappers/use-vehicle';
import useVehicleTransfer from '../../wrappers/use-vehicle-transfer';
import useFile from '../../wrappers/use-file';
import {
    addMachineHiddenInputStyles,
    addMachineSnackbarAnchorOrigin,
    addMachineStyles,
    addMachineTransferVinStyles,
    addMachineUploadIconStyles,
    addMachineVinInputStyles,
} from './AddMachine.styles';

// Datum spuštění aplikace – datum nákupu nelze zadat dříve.
const APP_LAUNCH_DATE = '2026-06-01';
// Prodlouženou záruku lze sjednat jen do tohoto počtu dnů od data nákupu.
const EXTENDED_WARRANTY_WINDOW_DAYS = 30;
// TODO: doplnit odkaz na podmínky prodloužené záruky, až budou k dispozici.
const EXTENDED_WARRANTY_TERMS_URL = '#';

const isPurchaseOlderThanWindow = (purchaseDate: string): boolean => {
    if (!purchaseDate) return false;
    const purchase = new Date(`${purchaseDate}T00:00:00`);
    const cutoff = new Date();
    cutoff.setHours(0, 0, 0, 0);
    cutoff.setDate(cutoff.getDate() - EXTENDED_WARRANTY_WINDOW_DAYS);
    return purchase < cutoff;
};

const AddMachine: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const formatDate = useDateFormat();
    const { user } = useAuth();
    const STEPS = [
        t('addMachine.steps.identification'),
        t('addMachine.steps.purchaseDetails'),
        t('addMachine.steps.documentation'),
        t('addMachine.steps.review'),
    ];
    const { checkVIN, create: createVehicle, get: getVehicle } = useVehicle();
    const { create: createTransfer } = useVehicleTransfer();
    const { create: createFile } = useFile();
    const [step, setStep] = useState(0);
    const [isVerifying, setIsVerifying] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isTransferMode, setIsTransferMode] = useState(false);
    const [showTransferDialog, setShowTransferDialog] = useState(false);
    const [existingVehicleGarageUid, setExistingVehicleGarageUid] = useState('');
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [formData, setFormData] = useState({
        vin: '',
        model: '',
        purchaseDate: '',
        dealer: '',
        extendedWarranty: false,
        invoice: null as File | null,
    });

    const handleVerifyVin = async () => {
        if (!formData.vin) return;
        setIsVerifying(true);
        try {
            const vin = formData.vin.toUpperCase();
            const vinExists = await checkVIN(vin);
            if (vinExists) {
                const existingVehicle = await getVehicle(vin);
                setExistingVehicleGarageUid(existingVehicle?.garageUid ?? '');
                setShowTransferDialog(true);
                return;
            }
            let modelName = 'Neznámý model';
            if (vin.startsWith('S')) modelName = 'Segway Snarler AT6 L';
            else if (vin.startsWith('T')) modelName = 'TGB Blade 1000';
            else if (vin.startsWith('L')) modelName = 'Linhai 500 Promax';
            setFormData(prev => ({ ...prev, model: modelName }));
            setStep(1);
        } catch {
            setErrorMsg(t('addMachine.vin.errorVerify'));
        } finally {
            setIsVerifying(false);
        }
    };

    const handleConfirmTransfer = () => {
        setShowTransferDialog(false);
        setIsTransferMode(true);
        setStep(2);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user?.garageUid) return;
        setIsSubmitting(true);

        if (isTransferMode) {
            if (!formData.invoice) return;
            try {
                const vin = formData.vin.toUpperCase();
                const transfer = await createTransfer({
                    createdBy: user.uid,
                    fromGarageUid: existingVehicleGarageUid,
                    toGarageUid: user.garageUid,
                    vehicleUid: vin,
                    fromUserUid: '',
                    toUserUid: user.uid,
                    status: 'pending',
                });
                await createFile(formData.invoice, {
                    vehicleUid: vin,
                    garageUid: user.garageUid,
                    vehicleTransferUid: transfer.id,
                });
                navigate('/app/garage');
            } catch {
                setErrorMsg(t('addMachine.transferMode.errorSubmit'));
            } finally {
                setIsSubmitting(false);
            }
            return;
        }

        try {
            const vin = formData.vin.toUpperCase();
            const newVehicle: Omit<IVehicle, 'createdAt'> = {
                vin,
                model: formData.model || 'Neznámý model',
                purchaseDate: formData.purchaseDate,
                dealer: formData.dealer,
                status: VehicleStatus.Pending,
                garageUid: user.garageUid,
                createdBy: user.uid,
                extendedWarrantyRequested: formData.extendedWarranty && !isPurchaseOlderThanWindow(formData.purchaseDate),
            };
            await createVehicle(newVehicle);
            if (formData.invoice) {
                await createFile(formData.invoice, {
                    vehicleUid: vin,
                    garageUid: user.garageUid,
                });
            }
            navigate('/app/garage');
        } catch {
            setErrorMsg(t('addMachine.normalMode.errorSubmit'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const warrantyLocked = isPurchaseOlderThanWindow(formData.purchaseDate);
    const extendedWarrantyControl = (
        <FormControlLabel
            sx={[addMachineStyles.warrantyCheckbox, warrantyLocked && addMachineStyles.warrantyCheckboxDisabled]}
            control={
                <Checkbox
                    checked={formData.extendedWarranty && !warrantyLocked}
                    disabled={warrantyLocked}
                    onChange={(e) => setFormData({ ...formData, extendedWarranty: e.target.checked })}
                />
            }
            label={
                <Typography variant="body2">
                    {t('addMachine.extendedWarranty.label')}
                    <MuiLink
                        href={EXTENDED_WARRANTY_TERMS_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    >
                        {t('addMachine.extendedWarranty.conditionsLink')}
                    </MuiLink>
                </Typography>
            }
        />
    );

    return (
        <>
            <Snackbar open={!!errorMsg} autoHideDuration={5000} onClose={() => setErrorMsg(null)} anchorOrigin={addMachineSnackbarAnchorOrigin}>
                <MuiAlert severity="error" onClose={() => setErrorMsg(null)}>{errorMsg}</MuiAlert>
            </Snackbar>

            <Box sx={addMachineStyles.root}>
                <Breadcrumbs>
                    <Box component={Link} to="/app" sx={addMachineStyles.homeBreadcrumbLink}>
                        <Home size={20} />
                    </Box>
                    <Box component={Link} to="/app/garage" sx={addMachineStyles.garageBreadcrumbLink}>
                        {t('garage.title')}
                    </Box>
                    <Typography variant="body2" color="text.primary">{t('addMachine.breadcrumb')}</Typography>
                </Breadcrumbs>

                <Typography variant="h4" fontWeight={700}>{t('addMachine.title')}</Typography>

                <Paper elevation={0} sx={addMachineStyles.containerPaper}>
                    <Stepper activeStep={step} alternativeLabel sx={addMachineStyles.stepper}>
                        {STEPS.map((label, index) => (
                            <Step key={index}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>

                    <Box component="form" onSubmit={handleSubmit} sx={addMachineStyles.form}>
                        {/* Step 1: VIN */}
                        {step === 0 && (
                            <Box sx={addMachineStyles.stepColumn}>
                                <TextField
                                    label={t('addMachine.vin.label')}
                                    required
                                    fullWidth
                                    placeholder={t('addMachine.vin.placeholder')}
                                    value={formData.vin}
                                    onChange={(e) => setFormData({ ...formData, vin: e.target.value.toUpperCase() })}
                                    InputProps={{ startAdornment: <InputAdornment position="start"><Search size={20} /></InputAdornment> }}
                                    inputProps={{ style: addMachineVinInputStyles }}
                                />
                                <Alert severity="info" icon={<Info size={20} />}>
                                    {t('addMachine.vin.hint')}
                                </Alert>
                                <Box sx={addMachineStyles.stepActionsEnd}>
                                    <Button
                                        variant="contained"
                                        onClick={handleVerifyVin}
                                        disabled={!formData.vin || isVerifying}
                                    >
                                        {isVerifying ? t('addMachine.vin.verifying') : t('addMachine.vin.verify')}
                                    </Button>
                                </Box>
                            </Box>
                        )}

                        {/* Step 2: Details (only in normal mode) */}
                        {step === 1 && !isTransferMode && (
                            <Box sx={addMachineStyles.stepColumn}>
                                <Alert severity="info">
                                    <Typography variant="body2" fontWeight={600}>{t('addMachine.identifiedModel')}</Typography>
                                    <Typography variant="body2">{formData.model}</Typography>
                                </Alert>

                                <TextField
                                    label={t('addMachine.purchaseDate')}
                                    type="date"
                                    required
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ min: APP_LAUNCH_DATE }}
                                    value={formData.purchaseDate}
                                    onChange={(e) => setFormData({ ...formData, purchaseDate: e.target.value })}
                                    error={!!formData.purchaseDate && formData.purchaseDate < APP_LAUNCH_DATE}
                                    helperText={
                                        !!formData.purchaseDate && formData.purchaseDate < APP_LAUNCH_DATE
                                            ? t('addMachine.purchaseDateTooEarly')
                                            : t('addMachine.purchaseDateHint')
                                    }
                                />

                                <TextField
                                    label={t('addMachine.dealer')}
                                    required
                                    fullWidth
                                    value={formData.dealer}
                                    onChange={(e) => setFormData({ ...formData, dealer: e.target.value })}
                                />

                                <Box sx={addMachineStyles.stepActionsBetween}>
                                    <Button variant="outlined" onClick={() => setStep(0)}>{t('addMachine.back')}</Button>
                                    <Button
                                        variant="contained"
                                        onClick={() => setStep(2)}
                                        disabled={!formData.purchaseDate || formData.purchaseDate < APP_LAUNCH_DATE || !formData.dealer}
                                    >
                                        {t('addMachine.continue')}
                                    </Button>
                                </Box>
                            </Box>
                        )}

                        {/* Step 3: Documents */}
                        {step === 2 && (
                            <Box sx={addMachineStyles.stepColumn}>
                                {isTransferMode && (
                                    <Alert severity="warning" icon={<ArrowRightLeft size={20} />}>
                                        {t('addMachine.transferMode.docHint')}
                                    </Alert>
                                )}
                                <Box>
                                    <Typography variant="body2" fontWeight={500} mb={1}>
                                        {isTransferMode ? t('addMachine.transferMode.docLabel') : t('addMachine.normalMode.docLabel')}
                                    </Typography>
                                    {!isTransferMode && (
                                        <Typography variant="body2" color="text.secondary" mb={2}>{t('addMachine.normalMode.docHint')}</Typography>
                                    )}

                                    <Box
                                        component="label"
                                        htmlFor="file-upload"
                                        sx={addMachineStyles.uploadZone}
                                    >
                                        <FileUp size={48} color="#bdbdbd" style={addMachineUploadIconStyles} />
                                        <Typography variant="body2" color="primary.main" fontWeight={500}>{t('addMachine.upload.button')}</Typography>
                                        <Typography variant="body2" color="text.secondary">{t('addMachine.upload.dragHint')}</Typography>
                                        <Typography variant="caption" color="text.disabled" mt={0.5}>{t('addMachine.upload.sizeHint')}</Typography>
                                        <input
                                            id="file-upload"
                                            type="file"
                                            style={addMachineHiddenInputStyles}
                                            onChange={(e) => setFormData({ ...formData, invoice: e.target.files ? e.target.files[0] : null })}
                                        />
                                    </Box>

                                    {formData.invoice && (
                                        <Alert severity="success" icon={<CircleCheck size={20} />} sx={addMachineStyles.selectedFileAlert}>
                                            {t('addMachine.upload.selectedFile', { name: formData.invoice.name })}
                                        </Alert>
                                    )}
                                </Box>

                                <Box sx={addMachineStyles.stepActionsBetween}>
                                    <Button variant="outlined" onClick={() => setStep(isTransferMode ? 0 : 1)}>{t('addMachine.back')}</Button>
                                    <Button
                                        variant="contained"
                                        onClick={() => setStep(3)}
                                        disabled={!formData.invoice}
                                    >
                                        {t('addMachine.continue')}
                                    </Button>
                                </Box>
                            </Box>
                        )}

                        {/* Step 4: Review */}
                        {step === 3 && (
                            <Box sx={addMachineStyles.stepColumn}>
                                <Paper variant="outlined" sx={addMachineStyles.reviewPaper}>
                                    <Typography variant="subtitle1" fontWeight={600} mb={3}>
                                        {isTransferMode ? t('addMachine.transferMode.reviewTitle') : t('addMachine.normalMode.reviewTitle')}
                                    </Typography>

                                    {isTransferMode ? (
                                        <Box sx={addMachineStyles.reviewGrid}>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">{t('addMachine.transferMode.reviewVinLabel')}</Typography>
                                                <Typography variant="body2" fontWeight={500} fontFamily="monospace">{formData.vin}</Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">{t('addMachine.transferMode.reviewTypeLabel')}</Typography>
                                                <Typography variant="body2" fontWeight={500}>{t('addMachine.transferMode.reviewTypeValue')}</Typography>
                                            </Box>
                                            <Box sx={addMachineStyles.reviewFullRow}>
                                                <Typography variant="caption" color="text.secondary">{t('addMachine.transferMode.reviewDocLabel')}</Typography>
                                                <Box sx={addMachineStyles.reviewFileRow}>
                                                    <FileText size={20} color="#757575" />
                                                    <Typography variant="body2">{formData.invoice?.name}</Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    ) : (
                                        <Box sx={addMachineStyles.reviewGrid}>
                                            {[
                                                { label: t('addMachine.normalMode.reviewVinLabel'), value: formData.vin, mono: true },
                                                { label: t('addMachine.normalMode.reviewModelLabel'), value: formData.model },
                                                { label: t('addMachine.normalMode.reviewDateLabel'), value: formatDate(formData.purchaseDate) },
                                                { label: t('addMachine.normalMode.reviewDealerLabel'), value: formData.dealer },
                                            ].map((item) => (
                                                <Box key={item.label}>
                                                    <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                                                    <Typography variant="body2" fontWeight={500} fontFamily={item.mono ? 'monospace' : undefined}>
                                                        {item.value}
                                                    </Typography>
                                                </Box>
                                            ))}
                                            <Box sx={addMachineStyles.reviewFullRow}>
                                                <Typography variant="caption" color="text.secondary">{t('addMachine.normalMode.reviewDocLabel')}</Typography>
                                                <Box sx={addMachineStyles.reviewFileRow}>
                                                    <FileText size={20} color="#757575" />
                                                    <Typography variant="body2">{formData.invoice?.name}</Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    )}
                                </Paper>

                                {!isTransferMode && (
                                    warrantyLocked ? (
                                        <Tooltip title={t('addMachine.extendedWarranty.lockedTooltip')}>
                                            <span>{extendedWarrantyControl}</span>
                                        </Tooltip>
                                    ) : (
                                        extendedWarrantyControl
                                    )
                                )}

                                <Box sx={addMachineStyles.stepActionsBetween}>
                                    <Button variant="outlined" onClick={() => setStep(2)}>{t('addMachine.back')}</Button>
                                    <Button type="submit" variant="contained" color="success" disabled={isSubmitting}>
                                        {isSubmitting
                                            ? t('addMachine.submitting')
                                            : isTransferMode
                                                ? t('addMachine.transferMode.submitButton')
                                                : t('addMachine.normalMode.submitButton')}
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Paper>

                {/* Dialog: VIN already exists */}
                <Dialog open={showTransferDialog} onClose={() => setShowTransferDialog(false)} maxWidth="sm" fullWidth>
                    <DialogTitle sx={addMachineStyles.transferDialogTitle}>
                        <ArrowRightLeft size={22} />
                        {t('addMachine.transferDialog.title')}
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" color="text.secondary">
                            {t('addMachine.transferDialog.body1Prefix')} <strong style={addMachineTransferVinStyles}>{formData.vin}</strong> {t('addMachine.transferDialog.body1Suffix')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mt={1.5}>
                            {t('addMachine.transferDialog.body2')}
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={addMachineStyles.transferDialogActions}>
                        <Button onClick={() => setShowTransferDialog(false)}>{t('addMachine.transferDialog.cancelButton')}</Button>
                        <Button variant="contained" startIcon={<ArrowRightLeft size={18} />} onClick={handleConfirmTransfer}>
                            {t('addMachine.transferDialog.confirmButton')}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </>
    );
};

export default AddMachine;
